import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import { Menu } from '@mui/material';
import { BookOpenText, Sparkles } from 'lucide-react';
import { OptionsMenuItem, PrimaryPlusIconButton } from 'src/components';
import { queuePromptDraft, requestPromptDraft } from 'src/components/ai/chat/chat-state';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai';
import { createNewPlaybookPrompt } from 'src/editor/toolbars/right-panel/ai/playbooks/generate-playbook-prompt';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

import { useCreatePlaybookAndEdit } from './use-create-playbook-and-edit';

export function NewPlaybookButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  const { openDrawer } = useGlobalDrawer();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { createPlaybookAndEdit, loading } = useCreatePlaybookAndEdit();
  const createWithAi = useCallback(() => {
    setOpen(false);
    const prompt = createNewPlaybookPrompt();
    const isLeavingExistingThread = Boolean(aiThreadIdVar());
    aiThreadIdVar(null);
    openDrawer();
    if (isLeavingExistingThread) queuePromptDraft(prompt);
    else requestPromptDraft(prompt);
  }, [openDrawer]);
  const createBlank = useCallback(() => {
    setOpen(false);
    void createPlaybookAndEdit();
  }, [createPlaybookAndEdit]);

  return (
    <>
      <PrimaryPlusIconButton
        ref={setAnchorEl}
        data-cy={Select.playbooks.newPlaybookButton}
        disabled={loading}
        tooltip="New Playbook"
        onClick={() => setOpen(current => !current)}
      />
      <Menu
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        <OptionsMenuItem
          clickHandler={createBlank}
          icon={<BookOpenText size={16} />}
          text="Create blank playbook"
        />
        <OptionsMenuItem
          clickHandler={createWithAi}
          icon={<Sparkles size={16} />}
          text="Create playbook with AI"
        />
      </Menu>
    </>
  );
}
